<template>
  <section class="unlock" :class="{ 'unlock--compact': compact }" aria-labelledby="unlock-title">
    <div class="unlock-head">
      <span class="unlock-eyebrow">Next step · free</span>
      <h2 id="unlock-title" class="unlock-title">Get the {{ checkCount }} checks behind your {{ formattedScore }}</h2>
    </div>
    <div v-if="!compact">
      <p class="unlock-text">
        Every check, what it means and how to fix it<template v-if="fixCount">, with the {{ fixCount }} fixes ranked by impact</template>.
        We email you a link to the full report that you can share with your team.
      </p>
      <ul class="unlock-benefits">
        <li v-for="benefit in benefits" :key="benefit"><ph-icon name="check" :size="16" />{{ benefit }}</li>
      </ul>
    </div>
    <div v-if="sentTo" ref="sent" class="unlock-sent" role="status" tabindex="-1">
      <span class="unlock-sent-icon" aria-hidden="true"><ph-icon name="envelope-simple" :size="22" /></span>
      <div>
        <p class="unlock-sent-title">Check your inbox</p>
        <p class="unlock-sent-text">We sent the link to your full report to <strong>{{ sentTo }}</strong>. It can take a minute to arrive.</p>
        <button type="button" class="unlock-sent-retry" @click="useAnotherEmail">Use another email address</button>
      </div>
    </div>
    <form v-else class="unlock-form" novalidate @submit.prevent="submit">
      <label for="unlock-email" class="visually-hidden">Work email</label>
      <div class="unlock-controls">
        <input
          id="unlock-email"
          ref="email"
          v-model.trim="email"
          class="ex-input unlock-input"
          type="email"
          name="email"
          autocomplete="email"
          placeholder="you@company.com"
          required
          maxlength="255"
          :readonly="sending"
          :aria-invalid="error ? 'true' : 'false'"
          aria-describedby="unlock-message unlock-note"
        />
        <button type="submit" class="ex-btn ex-btn--primary unlock-button" :disabled="sending" :aria-busy="sending ? 'true' : 'false'">
          <template v-if="sending">
            <ph-icon name="spinner-gap" :size="18" class="unlock-spinner" />
            Sending
          </template>
          <span v-else class="ex-btn__label" data-label="Receive full report">Receive full report</span>
        </button>
      </div>
      <p id="unlock-message" class="unlock-error" aria-live="polite">{{ error }}</p>
      <p id="unlock-note" class="unlock-note">
        We will email you the link to your full report.
        <a :href="privacyUrl" target="_blank" rel="noopener">Privacy policy</a>
      </p>
    </form>
  </section>
</template>

<script>
import PhIcon from './PhIcon.vue'
import { CATEGORIES, countWord, formatScore } from '../score/report.mjs'
import { PRIVACY_URL } from '../score/links'
import { EVENTS, errorStatus, trackEvent } from '../score/analytics'

const ERRORS = Object.freeze({
  empty: 'Enter your email address',
  invalid: 'Enter an email address like name@company.com',
  notWork: 'Use your work email, like name@company.com',
  failed: "We couldn't send the report. Try again."
})

export default {
  name: 'EmailUnlock',
  components: { PhIcon },
  props: {
    score: { type: [Number, String], required: true },
    // For copy, for example "50+".
    checkCount: { type: String, required: true },
    // Unknown while the result holds no checks.
    fixCount: { type: Number, default: null },
    uri: { type: String, required: true },
    resultUri: { type: String, required: true },
    publicJsonUri: { type: String, required: true },
    // A light, single-column version without the explanation, for the Chrome extension.
    compact: { type: Boolean, default: false }
  },
  data () {
    return { email: '', error: '', sending: false, sentTo: '', privacyUrl: PRIVACY_URL }
  },
  computed: {
    formattedScore () {
      return formatScore(this.score)
    },
    benefits () {
      return ['Your five biggest fixes first', `All ${this.checkCount} checks across ${countWord(CATEGORIES.length)} categories`, 'Share with your developer']
    }
  },
  methods: {
    async submit () {
      const input = this.$refs.email
      this.error = !this.email ? ERRORS.empty : (!input.validity.valid ? ERRORS.invalid : '')
      if (this.error) {
        input.focus()
        return
      }

      this.sending = true
      try {
        await this.$api.assets('/score/receive-full-report', {
          email: this.email,
          auditUri: this.uri,
          scoreResultUri: this.resultUri,
          auditJsonUri: this.publicJsonUri,
          score: this.score,
          lang: document.documentElement.getAttribute('lang') ?? 'en-EN'
        })
        trackEvent(EVENTS.fullReportRequested)
        this.sentTo = this.email
        await this.$nextTick()
        this.$refs.sent.focus()
      } catch (e) {
        const reason = e.response?.data?.error
        const notWorkEmail = typeof reason === 'string' && reason.includes('email')
        trackEvent(EVENTS.assetGeneratorError, {
          endpoint: '/score/receive-full-report',
          status: notWorkEmail ? 'not a work email' : errorStatus(e)
        })
        this.error = notWorkEmail ? ERRORS.notWork : ERRORS.failed
        input.focus()
      } finally {
        this.sending = false
      }
    },
    async useAnotherEmail () {
      this.sentTo = ''
      this.email = ''
      await this.$nextTick()
      this.$refs.email.focus()
    }
  }
}
</script>

<style scoped>
.unlock {
  display: grid;
  grid-template-columns: 1fr 460px;
  gap: 16px 40px;
  align-items: center;
  padding: 40px;
  border-radius: var(--ex-radius-panel-large);
  background: var(--ex-color-dark);
  color: var(--ex-color-white);
}

.unlock-head {
  grid-column: 1 / -1;
}

.unlock-eyebrow {
  font-size: 13px;
  font-weight: 600;
  color: var(--ex-color-mint);
}

.unlock-title {
  margin-top: 10px;
  font-size: 34px;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: var(--ex-color-white);
}

.unlock-text {
  font-size: 16px;
  line-height: 1.55;
  color: var(--ex-color-neutral-100);
}

.unlock-benefits {
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}

.unlock-benefits li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 5px 0;
  font-size: 14px;
  line-height: 1.5;
}

.unlock-benefits .ph-icon {
  margin-top: 3px;
  color: var(--ex-color-mint);
}

.unlock-controls {
  display: flex;
  gap: 8px;
}

.unlock-input {
  flex: 1;
  min-width: 0;
  height: 52px;
  border-color: var(--ex-color-white);
  font-size: 16px;
}

.unlock-button {
  height: 52px;
}

.unlock-button:disabled {
  background: var(--ex-color-blue);
  border-color: var(--ex-color-blue);
  color: var(--ex-color-white);
  cursor: progress;
}

.unlock-error {
  min-height: 0;
  margin-top: 8px;
  font-size: 14px;
  color: var(--ex-color-error-surface);
}

.unlock-error:empty {
  margin-top: 0;
}

.unlock-sent {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 20px 24px;
  border-radius: var(--ex-radius-tile);
  background: var(--ex-color-on-dark-faint);
}

.unlock-sent:focus,
.unlock-sent:focus-visible {
  outline: none;
  box-shadow: none;
}

.unlock-sent-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-mint);
  color: var(--ex-color-dark);
}

.unlock-sent-title {
  font-family: var(--ex-font-heading);
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--ex-color-white);
}

.unlock-sent-text {
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ex-color-neutral-100);
  overflow-wrap: anywhere;
}

.unlock-sent-text strong {
  font-weight: 600;
  color: var(--ex-color-white);
}

.unlock-sent-retry {
  margin-top: 10px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ex-color-white);
  font: inherit;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
}

.unlock-note {
  margin-top: 10px;
  font-size: 12px;
  color: var(--ex-color-neutral-300);
}

.unlock-note a {
  color: var(--ex-color-neutral-100);
}

/* Compact: light card for the narrow embed in the Chrome extension. */
.unlock--compact {
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 20px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-panel);
  background: var(--ex-color-white);
  color: var(--ex-color-dark);
}

.unlock--compact .unlock-eyebrow {
  color: var(--ex-color-blue);
}

.unlock--compact .unlock-title {
  font-size: 20px;
  color: var(--ex-color-dark);
}

.unlock--compact .unlock-input {
  border-color: var(--ex-color-neutral-100);
}

.unlock--compact .unlock-error {
  color: var(--ex-color-error-foreground);
}

.unlock--compact .unlock-note {
  color: var(--ex-color-neutral-500);
}

.unlock--compact .unlock-note a {
  color: var(--ex-color-neutral-700);
}

.unlock--compact .unlock-sent {
  background: var(--ex-color-surface-blue);
}

.unlock--compact .unlock-sent-icon {
  background: var(--ex-color-blue-light);
  color: var(--ex-color-blue);
}

.unlock--compact .unlock-sent-title,
.unlock--compact .unlock-sent-text strong,
.unlock--compact .unlock-sent-retry {
  color: var(--ex-color-dark);
}

.unlock--compact .unlock-sent-text {
  color: var(--ex-color-neutral-700);
}

@media (prefers-reduced-motion: no-preference) {
  .unlock-spinner {
    animation: unlock-spin 1s linear infinite;
  }
}

@keyframes unlock-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 960px) {
  .unlock {
    grid-template-columns: 1fr;
    padding: 28px 24px;
  }

  .unlock-title {
    font-size: 28px;
  }
}

@media (max-width: 640px) {
  .unlock-controls {
    flex-direction: column;
  }
}
</style>
